import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { LayoutGrid, List } from "lucide-react";
import React from "react";

export type ViewMode = "list" | "grid";

interface ViewModeToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  /** Tooltip + aria label for the grid option ("Card view" on workspace pages). */
  gridLabel?: string;
  listLabel?: string;
  className?: string;
}

/**
 * The segmented list/grid switch used on browse surfaces (workspace detail
 * sections, home browser). Callers whose persisted state says "card" map it
 * to "grid" at the boundary.
 */
export const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  value,
  onChange,
  gridLabel = "Grid view",
  listLabel = "List view",
  className,
}) => (
  <div
    className={cn(
      "flex items-center rounded-full bg-muted/50 p-0.5",
      className,
    )}
  >
    <ViewModeToggleOption
      label={listLabel}
      pressed={value === "list"}
      onClick={() => onChange("list")}
      icon={<List className="h-4 w-4" />}
    />
    <ViewModeToggleOption
      label={gridLabel}
      pressed={value === "grid"}
      onClick={() => onChange("grid")}
      icon={<LayoutGrid className="h-4 w-4" />}
    />
  </div>
);

const ViewModeToggleOption: React.FC<{
  label: string;
  pressed: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}> = ({ label, pressed, onClick, icon }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        aria-label={label}
        aria-pressed={pressed}
        className={cn(
          "h-7 w-7 rounded-full",
          pressed
            ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
            : "text-foreground/70 hover:text-foreground",
        )}
        onClick={onClick}
      >
        {icon}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);
